/**
 * @fileoverview Role priority logic
 * 
 * @description
 * Determines which role is "primary" when user has multiple roles.
 * Higher priority role wins.
 * 
 * @example
 * // User with ['moderator', 'adopter'] → primary role is 'moderator'
 * const primary = getPrimaryRole(['moderator', 'adopter']); // 'moderator'
 */

import type { AppRole } from './types';

/**
 * Role priority order (index = priority, higher = more important)
 * 
 * Priority:
 * - user (0) - lowest
 * - adopter (1)
 * - curator (2)
 * - moderator (3)
 * - admin (4) - highest
 */
export const ROLE_PRIORITY: AppRole[] = ['user', 'adopter', 'curator', 'moderator', 'admin'];

/**
 * Get the primary (highest priority) role from a list of roles
 * 
 * @param roles - Array of user roles
 * @returns The highest priority role, or null if array is empty
 * 
 * @example
 * getPrimaryRole(['moderator', 'adopter']); // 'moderator'
 * getPrimaryRole(['admin', 'curator', 'moderator']); // 'admin'
 * getPrimaryRole([]); // null
 */
export function getPrimaryRole(roles: AppRole[]): AppRole | null {
  if (!roles.length) return null;
  
  return roles.reduce((highest, current) => {
    const currentPriority = ROLE_PRIORITY.indexOf(current);
    const highestPriority = ROLE_PRIORITY.indexOf(highest);
    return currentPriority > highestPriority ? current : highest;
  });
}
